import type { PropertyDiscovery } from "../../properties/common/propertyDiscovery.js";
import { Material } from "three";
import { IProperty, Property, PropertyMetadata } from "../../properties/common/properties.js";
import { isNodeLike, isInputNode, isConstNode, isVarNode, forceNodeMaterialRecompile, NodeLike, InputNodeLike, ConstNodeLike, VarNodeLike, type MaterialRecompileHandle } from "./helper.nodes.js";
import { IPropertyAccessorService } from "../../properties/common/accessor.js";
import { getMetadata, postprocessThreeProperty } from "./propertyProvider.metadata.js";
import { joinPath, splitPath } from "@needle-tools/addressing";
import { buildAddress, normalizeIncomingPath } from "../../properties/common/propertyPath.js";

/**
 * TSL Node Material properties that should be exposed in the inspector.
 * These are the node-based equivalents of traditional material properties.
 */
export interface TSLNodeMaterialProperties {
	// Core output nodes
	colorNode?: NodeLike;
	opacityNode?: NodeLike;
	alphaTestNode?: NodeLike;

	// Lighting nodes
	emissiveNode?: NodeLike;
	normalNode?: NodeLike;
	lightsNode?: NodeLike;
	envNode?: NodeLike;
	aoNode?: NodeLike;

	// PBR material nodes (MeshStandardNodeMaterial)
	metalnessNode?: NodeLike;
	roughnessNode?: NodeLike;

	// Physical material nodes (MeshPhysicalNodeMaterial)
	clearcoatNode?: NodeLike;
	clearcoatNormalNode?: NodeLike;
	clearcoatRoughnessNode?: NodeLike;
	sheenNode?: NodeLike;
	sheenColorNode?: NodeLike;
	sheenRoughnessNode?: NodeLike;
	iridescenceNode?: NodeLike;
	iridescenceIORNode?: NodeLike;
	iridescenceThicknessNode?: NodeLike;
	transmissionNode?: NodeLike;
	thicknessNode?: NodeLike;
	attenuationDistanceNode?: NodeLike;
	attenuationColorNode?: NodeLike;
	anisotropyNode?: NodeLike;

	// Vertex and geometry nodes
	positionNode?: NodeLike;
	geometryNode?: NodeLike;
	vertexNode?: NodeLike;

	// Fragment and output nodes
	fragmentNode?: NodeLike;
	depthNode?: NodeLike;
	outputNode?: NodeLike;

	// Special effect nodes
	backdropNode?: NodeLike;
	maskNode?: NodeLike;
	mrtNode?: NodeLike;

	// Shadow nodes
	castShadowNode?: NodeLike;
	maskShadowNode?: NodeLike;
	receivedShadowNode?: NodeLike;
}

/**
 * Property configuration for a TSL node property.
 * This defines how the property should be displayed and edited in the inspector.
 */
export interface TSLNodePropertyConfig {
	/**
	 * The property key on the material (e.g., "colorNode", "roughnessNode")
	 */
	key: keyof TSLNodeMaterialProperties;

	/**
	 * Display label in the inspector
	 */
	label: string;

	/**
	 * Description shown in tooltips
	 */
	description: string;

	/**
	 * Priority for ordering (higher = shown first)
	 */
	priority?: number;

	/**
	 * Category for grouping related properties
	 */
	category: "core" | "lighting" | "pbr" | "physical" | "vertex" | "fragment" | "effects" | "shadows";

	/**
	 * Expected node types (for validation and UI hints)
	 */
	expectedNodeTypes?: string[];

	/**
	 * Material types this property applies to
	 */
	materialTypes?: string[];
}

/**
 * Configuration for all TSL node properties that should be exposed in the inspector.
 * This is organized by category for better maintainability.
 */
export const TSL_NODE_PROPERTY_CONFIGS: readonly TSLNodePropertyConfig[] = [
	// Core output properties
	{
		key: "colorNode",
		label: "Color Node",
		description: "Controls the base color of the material using TSL nodes",
		priority: 100,
		category: "core",
		expectedNodeTypes: ["vec3", "vec4", "color"],
	},
	{
		key: "opacityNode",
		label: "Opacity Node",
		description: "Controls material transparency using TSL nodes",
		priority: 95,
		category: "core",
		expectedNodeTypes: ["float"],
	},
	{
		key: "alphaTestNode",
		label: "Alpha Test Node",
		description: "Threshold for alpha testing using TSL nodes",
		priority: 90,
		category: "core",
		expectedNodeTypes: ["float"],
	},

	// Lighting properties
	{
		key: "emissiveNode",
		label: "Emissive Node",
		description: "Controls emissive color using TSL nodes",
		priority: 85,
		category: "lighting",
		expectedNodeTypes: ["vec3", "color"],
	},
	{
		key: "normalNode",
		label: "Normal Node",
		description: "Custom normal mapping using TSL nodes",
		priority: 80,
		category: "lighting",
		expectedNodeTypes: ["vec3"],
	},
	{
		key: "lightsNode",
		label: "Lights Node",
		description: "Custom lighting calculation using TSL nodes",
		priority: 75,
		category: "lighting",
		expectedNodeTypes: ["vec3"],
	},
	{
		key: "envNode",
		label: "Environment Node",
		description: "Environment lighting using TSL nodes",
		priority: 70,
		category: "lighting",
		expectedNodeTypes: ["vec3"],
	},
	{
		key: "aoNode",
		label: "Ambient Occlusion Node",
		description: "Ambient occlusion using TSL nodes",
		priority: 65,
		category: "lighting",
		expectedNodeTypes: ["float"],
	},

	// PBR properties (MeshStandardNodeMaterial)
	{
		key: "metalnessNode",
		label: "Metalness Node",
		description: "Controls metallic property using TSL nodes (0=dielectric, 1=metallic)",
		priority: 60,
		category: "pbr",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshStandardNodeMaterial", "MeshPhysicalNodeMaterial"],
	},
	{
		key: "roughnessNode",
		label: "Roughness Node",
		description: "Controls surface roughness using TSL nodes (0=smooth, 1=rough)",
		priority: 55,
		category: "pbr",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshStandardNodeMaterial", "MeshPhysicalNodeMaterial"],
	},

	// Physical material properties
	{
		key: "clearcoatNode",
		label: "Clearcoat Node",
		description: "Clearcoat layer intensity using TSL nodes",
		priority: 50,
		category: "physical",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "clearcoatNormalNode",
		label: "Clearcoat Normal Node",
		description: "Normal map for clearcoat layer using TSL nodes",
		priority: 48,
		category: "physical",
		expectedNodeTypes: ["vec3"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "clearcoatRoughnessNode",
		label: "Clearcoat Roughness Node",
		description: "Roughness of clearcoat layer using TSL nodes",
		priority: 46,
		category: "physical",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "sheenNode",
		label: "Sheen Node",
		description: "Sheen effect intensity using TSL nodes",
		priority: 44,
		category: "physical",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "sheenColorNode",
		label: "Sheen Color Node",
		description: "Sheen tint color using TSL nodes",
		priority: 42,
		category: "physical",
		expectedNodeTypes: ["vec3", "color"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "sheenRoughnessNode",
		label: "Sheen Roughness Node",
		description: "Sheen roughness using TSL nodes",
		priority: 40,
		category: "physical",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "iridescenceNode",
		label: "Iridescence Node",
		description: "Iridescence effect intensity using TSL nodes",
		priority: 38,
		category: "physical",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "iridescenceIORNode",
		label: "Iridescence IOR Node",
		description: "Iridescence index of refraction using TSL nodes",
		priority: 36,
		category: "physical",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "iridescenceThicknessNode",
		label: "Iridescence Thickness Node",
		description: "Iridescence layer thickness using TSL nodes",
		priority: 34,
		category: "physical",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "transmissionNode",
		label: "Transmission Node",
		description: "Light transmission for glass-like materials using TSL nodes",
		priority: 32,
		category: "physical",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "thicknessNode",
		label: "Thickness Node",
		description: "Material thickness for transmission using TSL nodes",
		priority: 30,
		category: "physical",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "attenuationDistanceNode",
		label: "Attenuation Distance Node",
		description: "Light absorption distance using TSL nodes",
		priority: 28,
		category: "physical",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "attenuationColorNode",
		label: "Attenuation Color Node",
		description: "Light absorption color using TSL nodes",
		priority: 26,
		category: "physical",
		expectedNodeTypes: ["vec3", "color"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},
	{
		key: "anisotropyNode",
		label: "Anisotropy Node",
		description: "Anisotropic reflections using TSL nodes",
		priority: 24,
		category: "physical",
		expectedNodeTypes: ["float"],
		materialTypes: ["MeshPhysicalNodeMaterial"],
	},

	// Vertex properties
	{
		key: "positionNode",
		label: "Position Node",
		description: "Custom vertex position using TSL nodes",
		priority: 20,
		category: "vertex",
		expectedNodeTypes: ["vec3"],
	},
	{
		key: "geometryNode",
		label: "Geometry Node",
		description: "Custom geometry modifications using TSL nodes",
		priority: 18,
		category: "vertex",
	},
	{
		key: "vertexNode",
		label: "Vertex Node",
		description: "Custom vertex shader using TSL nodes",
		priority: 16,
		category: "vertex",
	},

	// Fragment properties
	{
		key: "fragmentNode",
		label: "Fragment Node",
		description: "Custom fragment shader using TSL nodes",
		priority: 14,
		category: "fragment",
	},
	{
		key: "depthNode",
		label: "Depth Node",
		description: "Custom depth output using TSL nodes",
		priority: 12,
		category: "fragment",
		expectedNodeTypes: ["float"],
	},
	{
		key: "outputNode",
		label: "Output Node",
		description: "Final output color using TSL nodes",
		priority: 10,
		category: "fragment",
		expectedNodeTypes: ["vec4"],
	},

	// Effects
	{
		key: "backdropNode",
		label: "Backdrop Node",
		description: "Backdrop effects using TSL nodes",
		priority: 8,
		category: "effects",
	},
	{
		key: "maskNode",
		label: "Mask Node",
		description: "Masking effects using TSL nodes",
		priority: 6,
		category: "effects",
		expectedNodeTypes: ["float"],
	},
	{
		key: "mrtNode",
		label: "MRT Node",
		description: "Multiple render target output using TSL nodes",
		priority: 4,
		category: "effects",
	},

	// Shadows
	{
		key: "castShadowNode",
		label: "Cast Shadow Node",
		description: "Custom shadow casting using TSL nodes",
		priority: 3,
		category: "shadows",
	},
	{
		key: "maskShadowNode",
		label: "Mask Shadow Node",
		description: "Shadow masking using TSL nodes",
		priority: 2,
		category: "shadows",
	},
	{
		key: "receivedShadowNode",
		label: "Received Shadow Node",
		description: "Custom shadow receiving using TSL nodes",
		priority: 1,
		category: "shadows",
	},
] as const;

/**
 * Information about a TSL node property extracted from a material.
 */
export interface TSLNodePropertyInfo {
	/**
	 * The property key (e.g., "colorNode")
	 */
	key: string;

	/**
	 * The node instance
	 */
	node: NodeLike;

	/**
	 * Configuration for this property
	 */
	config: TSLNodePropertyConfig;

	/**
	 * Full property path for addressing (e.g., "material.colorNode")
	 */
	propertyPath: string;
}

/**
 * Extracts TSL node properties from a NodeMaterial.
 * Returns an array of property information that can be used to create IProperty instances.
 *
 * @param material - The NodeMaterial to extract properties from
 * @param materialType - The type name of the material (e.g., "MeshStandardNodeMaterial")
 * @returns Array of TSL node property information
 */
export function extractTSLNodeProperties(material: Material, materialType: string): TSLNodePropertyInfo[] {
	const properties: TSLNodePropertyInfo[] = [];

	for (const config of TSL_NODE_PROPERTY_CONFIGS) {
		// Check if this property applies to the current material type
		if (config.materialTypes && !config.materialTypes.includes(materialType)) {
			continue;
		}

		// Check if the material has this property
		const propertyKey = config.key;
		if (!(propertyKey in material)) {
			continue;
		}

		const value = (material as any)[propertyKey];

		// Verify it's actually a node
		if (!isNodeLike(value)) {
			continue;
		}

		properties.push({
			key: propertyKey,
			node: value,
			config,
			propertyPath: `material.${propertyKey}`,
		});
	}

	return properties;
}

/**
 * Creates an IProperty instance for a TSL node property.
 * This handles the special requirements of TSL nodes, including proper typing and metadata.
 *
 * @param material - The material that owns this property
 * @param propertyInfo - Information about the TSL node property
 * @param objectId - The ID of the Object3D this material belongs to
 * @param accessorService - Service for property accessors
 * @returns An IProperty instance configured for the TSL node
 */
export function createTSLNodeProperty(
	material: Material,
	propertyInfo: TSLNodePropertyInfo,
	objectId: string | number,
	accessorService: IPropertyAccessorService,
	discovery?: PropertyDiscovery
): IProperty {
	const { key, node, config, propertyPath } = propertyInfo;
	const path = splitPath(normalizeIncomingPath(propertyPath) ?? propertyPath);
	if (objectId === material.uuid && path[0] === "material") path.shift();
	const addressOwner = objectId === material.uuid ? material : undefined;
	discovery?.watch(node);

	// Check if this is a VarNode (variable that wraps another node)
	// VarNode wraps another node, so we need to unwrap it and process the inner node
	if (isVarNode(node)) {
		const varNode = node as VarNodeLike;
		discovery?.member(varNode, "node", varNode.node);
		discovery?.depend(varNode, "global", varNode.global);
		discovery?.depend(varNode, "readOnly", varNode.readOnly);

		// Create a new property info for the inner node
		const innerPropertyInfo: TSLNodePropertyInfo = {
			...propertyInfo,
			node: varNode.node,
			propertyPath: joinPath([...path, "node"]),
		};

		// Recursively process the inner node
		const innerProperty = createTSLNodeProperty(material, innerPropertyInfo, objectId, accessorService, discovery);

		// Update the description to indicate this is a VarNode
		if (innerProperty.metadata.description) {
			const varType = varNode.global ? "Global Var" : "Local Var";
			const readOnlyStr = varNode.readOnly ? ", Read-only" : "";
			innerProperty.metadata.description = `${innerProperty.metadata.description} (${varType}${readOnlyStr})`;
		}

		return innerProperty;
	}

	// Check if this is a constant node (value baked into the shader).
	// Editable like an input node, but — unlike a live uniform — a const's value
	// is compiled into the shader as a literal, so a change only shows after the
	// material recompiles (wired below).
	if (isConstNode(node)) {
		const constNode = node as ConstNodeLike;
		const valueType = typeof constNode.value;

		// Determine property type based on actual value
		let propertyType: string = "object";
		if (valueType === "number") {
			propertyType = "number";
		} else if (valueType === "boolean") {
			propertyType = "boolean";
		} else if (valueType === "string") {
			propertyType = "string";
		}

		// Get metadata for the value
		const valueMeta = getMetadata(constNode, "value", constNode.value);

		// Build metadata
		const metadata: PropertyMetadata = {
			...valueMeta,
			address: buildAddress(String(objectId), [...path, "value"]), addressOwner,
			description: `${config.description} (Constant)`,
			priority: config.priority,
		};

		// Add node type information to description
		if (node.nodeType) {
			metadata.description = `${config.description} (Type: ${node.nodeType}, Constant)`;
		}

		const property = new Property(constNode, "value", propertyType, metadata, accessorService);
		discovery?.metadata(property, getMetadata, valueMeta);
		// Use the key name directly instead of custom label
		property.label = key;

		// Apply any additional three.js specific postprocessing
		postprocessThreeProperty(property);

		// A ConstNode's value is baked into the generated shader, so a live
		// uniform-style write won't show — the material must recompile. Do it on
		// COMMIT only: skip transient preview ticks (drag), where recompiling per
		// tick would rebuild the shader every frame. This runs regardless of
		// whether the nodegraph panel was ever opened (that path recompiles too;
		// a redundant rev bump is harmless).
		property.onDidChange((e) => {
			if (e.transient) return;
			forceNodeMaterialRecompile(material as unknown as MaterialRecompileHandle);
		});

		return property;
	}
	// Check if this is an input node (which has a direct value)
	else if (isInputNode(node)) {
		// Input nodes have a value property that can be edited directly
		// We need to create a property that targets the node's .value property
		const inputNode = node as InputNodeLike;
		const valueType = typeof inputNode.value;

		// Determine property type based on actual value
		let propertyType: string = "object";
		if (valueType === "number") {
			propertyType = "number";
		} else if (valueType === "boolean") {
			propertyType = "boolean";
		} else if (valueType === "string") {
			propertyType = "string";
		}

		// Get metadata for the value
		const valueMeta = getMetadata(inputNode, "value", inputNode.value);

		// Build metadata
		const metadata: PropertyMetadata = {
			...valueMeta,
			address: buildAddress(String(objectId), [...path, "value"]), addressOwner,
			description: config.description,
			priority: config.priority,
		};

		// Add node type information to description
		if (node.nodeType) {
			metadata.description = `${config.description} (Type: ${node.nodeType})`;
		}

		// Create property that targets the node's value property
		const property = new Property(inputNode, "value", propertyType, metadata, accessorService);
		discovery?.metadata(property, getMetadata, valueMeta);
		// Use the key name directly instead of custom label
		property.label = key;

		// Apply any additional three.js specific postprocessing
		postprocessThreeProperty(property);

		// When the value changes, mark the material as needing update
		property.onDidChange(() => {
			material.needsUpdate = true;
		});

		return property;
	} else {
		// For complex nodes, we expose them as read-only objects
		// In the future, this is where we'll integrate the graph visualization

		// Determine the property type
		let propertyType: string = "object";
		if (node.nodeType) {
			propertyType = node.nodeType;
		}

		const metadata: PropertyMetadata = {
			address: buildAddress(String(objectId), path), addressOwner,
			description: config.description,
			priority: config.priority,
			editable: false,
			writeable: false,
			readable: true,
			objectValuePlaceholderString: `TSL ${node.type || "Node"} (Complex Graph)`,
		};

		// Add node type information to description
		if (node.nodeType) {
			metadata.description = `${config.description} (Type: ${node.nodeType}, Complex Node)`;
		}

		const property = new Property(material, key, propertyType, metadata, accessorService);
		// Use the key name directly instead of custom label
		property.label = key;

		// Apply any additional three.js specific postprocessing
		postprocessThreeProperty(property);

		return property;
	}
}

/**
 * Gets the category display name for grouping TSL properties.
 */
export function getTSLCategoryDisplayName(category: TSLNodePropertyConfig["category"]): string {
	switch (category) {
		case "core": return "Core";
		case "lighting": return "Lighting";
		case "pbr": return "PBR";
		case "physical": return "Physical";
		case "vertex": return "Vertex";
		case "fragment": return "Fragment";
		case "effects": return "Effects";
		case "shadows": return "Shadows";
		default: return "Other";
	}
}
